/**
 * API Client
 * A flexible and reusable API client for making network requests
 */

import {
  ApiClientConfig,
  ApiError,
  ApiResponse,
  AuthTokens,
  ErrorInterceptor,
  ErrorNotificationHandler,
  HttpMethod,
  LoadingStateChangeHandler,
  RequestConfig,
  RequestInterceptor,
  ResponseInterceptor,
  SuccessNotificationHandler,
} from './types';

/**
 * ApiClient class for making HTTP requests
 */
export class ApiClient {
  private baseURL: string;
  private defaultHeaders: Record<string, string>;
  private timeout: number;
  private requestInterceptors: RequestInterceptor[] = [];
  private responseInterceptors: ResponseInterceptor[] = [];
  private errorInterceptors: ErrorInterceptor[] = [];
  private loadingStateChangeHandlers: LoadingStateChangeHandler[] = [];
  private successNotificationHandlers: SuccessNotificationHandler[] = [];
  private errorNotificationHandlers: ErrorNotificationHandler[] = [];
  private activeRequests = new Map<string, AbortController>();
  private pendingRequests = new Map<string, Promise<unknown>>();

  /**
   * Creates a new ApiClient instance
   * @param config - Configuration for the API client
   */
  constructor(config: ApiClientConfig) {
    this.baseURL = config.baseURL;
    this.defaultHeaders = config.headers || {};
    this.timeout = config.timeout || 30000; // Default timeout: 30 seconds
  }

  /**
   * Adds a request interceptor
   * @param interceptor - Function to modify request config before sending
   */
  addRequestInterceptor(interceptor: RequestInterceptor): void {
    this.requestInterceptors.push(interceptor);
  }

  /**
   * Adds a response interceptor
   * @param interceptor - Function to process successful responses
   */
  addResponseInterceptor(interceptor: ResponseInterceptor): void {
    this.responseInterceptors.push(interceptor);
  }

  /**
   * Adds an error interceptor
   * @param interceptor - Function to process errors
   */
  addErrorInterceptor(interceptor: ErrorInterceptor): void {
    this.errorInterceptors.push(interceptor);
  }

  /**
   * Adds a loading state change handler
   * @param handler - Function to handle loading state changes
   */
  addLoadingStateChangeHandler(handler: LoadingStateChangeHandler): void {
    this.loadingStateChangeHandlers.push(handler);
  }

  /**
   * Adds a success notification handler
   * @param handler - Function to handle success notifications
   */
  addSuccessNotificationHandler(handler: SuccessNotificationHandler): void {
    this.successNotificationHandlers.push(handler);
  }

  /**
   * Adds an error notification handler
   * @param handler - Function to handle error notifications
   */
  addErrorNotificationHandler(handler: ErrorNotificationHandler): void {
    this.errorNotificationHandlers.push(handler);
  }

  /**
   * Sets authentication tokens for requests
   * @param tokens - Authentication tokens
   */
  setAuthTokens(tokens: AuthTokens | null): void {
    if (tokens) {
      this.defaultHeaders.Authorization = `${tokens.tokenType} ${tokens.accessToken}`;
    } else {
      delete this.defaultHeaders.Authorization;
    }
  }

  /**
   * Makes a GET request
   * @param url - The URL to request
   * @param params - Query parameters
   * @param config - Additional request configuration
   * @returns Promise with the response
   */
  async get<T = unknown, P = Record<string, unknown>>(
    url: string,
    params?: P,
    config?: Partial<RequestConfig>
  ): Promise<ApiResponse<T>> {
    return this.request<T>({
      method: HttpMethod.GET,
      url,
      params: params as Record<string, string | number | boolean | undefined>,
      ...config,
    });
  }

  /**
   * Makes a POST request
   * @param url - The URL to request
   * @param data - The data to send
   * @param config - Additional request configuration
   * @returns Promise with the response
   */
  async post<T = unknown, D = unknown>(
    url: string,
    data?: D,
    config?: Partial<RequestConfig>
  ): Promise<ApiResponse<T>> {
    return this.request<T>({
      method: HttpMethod.POST,
      url,
      data,
      ...config,
    });
  }

  /**
   * Makes a PUT request
   * @param url - The URL to request
   * @param data - The data to send
   * @param config - Additional request configuration
   * @returns Promise with the response
   */
  async put<T = unknown, D = unknown>(
    url: string,
    data?: D,
    config?: Partial<RequestConfig>
  ): Promise<ApiResponse<T>> {
    return this.request<T>({
      method: HttpMethod.PUT,
      url,
      data,
      ...config,
    });
  }

  /**
   * Makes a DELETE request
   * @param url - The URL to request
   * @param config - Additional request configuration
   * @returns Promise with the response
   */
  async delete<T = unknown>(
    url: string,
    config?: Partial<RequestConfig>
  ): Promise<ApiResponse<T>> {
    return this.request<T>({
      method: HttpMethod.DELETE,
      url,
      ...config,
    });
  }

  /**
   * Makes a PATCH request
   * @param url - The URL to request
   * @param data - The data to send
   * @param config - Additional request configuration
   * @returns Promise with the response
   */
  async patch<T = unknown, D = unknown>(
    url: string,
    data?: D,
    config?: Partial<RequestConfig>
  ): Promise<ApiResponse<T>> {
    return this.request<T>({
      method: HttpMethod.PATCH,
      url,
      data,
      ...config,
    });
  }

  /**
   * Uploads a file or form data
   * @param url - The URL to request
   * @param formData - The FormData object to send
   * @param config - Additional request configuration
   * @returns Promise with the response
   */
  async upload<T = unknown>(
    url: string,
    formData: FormData,
    config?: Partial<RequestConfig>
  ): Promise<ApiResponse<T>> {
    return this.request<T>({
      method: HttpMethod.POST,
      url,
      data: formData,
      formData: true,
      ...config,
    });
  }

  /**
   * Cancels a request by its ID
   * @param requestId - The ID of the request to cancel
   */
  cancelRequest(requestId: string): void {
    const controller = this.activeRequests.get(requestId);
    if (controller) {
      controller.abort();
      this.activeRequests.delete(requestId);
    }
  }

  /**
   * Cancels all active requests
   */
  cancelAllRequests(): void {
    this.activeRequests.forEach((controller) => {
      controller.abort();
    });
    this.activeRequests.clear();
  }

  /**
   * Makes an HTTP request
   * @param config - Request configuration
   * @returns Promise with the response
   */
  private async request<T = unknown>(config: RequestConfig): Promise<ApiResponse<T>> {
    // Apply request interceptors first to get the processed config
    let processedConfig = { ...config };
    for (const interceptor of this.requestInterceptors) {
      processedConfig = await interceptor(processedConfig);
    }

    // Build the full URL
    const url = this.buildUrl(processedConfig.url, processedConfig.params);

    // Generate a key for this request
    const requestKey = this.generateRequestKey(
      processedConfig.method,
      url,
      processedConfig.method !== HttpMethod.GET ? processedConfig.data : undefined,
      processedConfig.headers?.Authorization
    );

    // Check if an identical request is already in progress
    const existingRequest = this.pendingRequests.get(requestKey);
    if (existingRequest) {
      // Return the existing request promise
      return existingRequest as Promise<ApiResponse<T>>;
    }

    // Generate a unique ID for this request
    const requestId = Date.now().toString() + Math.random().toString(36).substring(2, 9);

    // Create an AbortController for this request
    const controller = new AbortController();
    if (!config.signal) {
      config.signal = controller.signal;
    }
    this.activeRequests.set(requestId, controller);

    // Set up timeout if specified
    const timeoutId = setTimeout(() => {
      this.cancelRequest(requestId);
    }, config.timeout || this.timeout);

    // Create a promise for this request and store it
    const requestPromise = this.executeRequest<T>(requestId, processedConfig, url, timeoutId);
    this.pendingRequests.set(requestKey, requestPromise);

    try {
      // Wait for the request to complete
      const response = await requestPromise;
      return response;
    } finally {
      // Remove the request from pendingRequests when it completes
      this.pendingRequests.delete(requestKey);
    }
  }

  private async executeRequest<T>(
    requestId: string,
    processedConfig: RequestConfig,
    url: string,
    timeoutId: NodeJS.Timeout
  ): Promise<ApiResponse<T>> {
    try {
      // Notify loading state handlers
      this.notifyLoadingStateHandlers(true, requestId);

      // Prepare headers
      const headers: Record<string, string> = {
        ...this.defaultHeaders,
        ...processedConfig.headers,
      };

      // Don't set Content-Type for FormData (browser will set it with boundary)
      if (processedConfig.formData) {
        delete headers['Content-Type'];
      } else if (processedConfig.data && !headers['Content-Type']) {
        headers['Content-Type'] = 'application/json';
      }

      // Prepare request options
      const requestOptions: RequestInit = {
        method: processedConfig.method,
        headers,
        signal: processedConfig.signal,
        credentials: processedConfig.withCredentials ? 'include' : 'same-origin',
      };

      // Add body if not a GET request
      if (processedConfig.method !== HttpMethod.GET && processedConfig.data) {
        if (processedConfig.formData) {
          requestOptions.body = processedConfig.data as FormData;
        } else {
          requestOptions.body = JSON.stringify(processedConfig.data);
        }
      }

      // Make the fetch request
      const response = await fetch(url, requestOptions);

      // Parse the response
      let data: T;
      const contentType = response.headers.get('Content-Type') || '';

      if (contentType.includes('application/json')) {
        data = await response.json();
      } else if (contentType.includes('text/')) {
        data = await response.text() as unknown as T;
      } else {
        data = await response.blob() as unknown as T;
      }

      // Create the API response object
      const apiResponse: ApiResponse<T> = {
        data,
        status: response.status,
        statusText: response.statusText,
        headers: this.parseHeaders(response.headers),
      };

      // Handle non-2xx responses
      if (!response.ok) {
        throw this.createApiError(apiResponse);
      }

      // Apply response interceptors
      let processedResponse = apiResponse;
      for (const interceptor of this.responseInterceptors) {
        processedResponse = await interceptor(processedResponse);
      }

      // Notify success handlers
      this.notifySuccessHandlers(processedResponse, processedConfig);

      return processedResponse;
    } catch (error) {
      // Handle errors
      let apiError: ApiError;

      const requestId = `client-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      const timestamp = new Date().toISOString();
      const path = url.replace(this.baseURL, '');

      if (error instanceof DOMException && error.name === 'AbortError') {
        apiError = {
          message: 'Request was cancelled',
          code: 'CLIENT_REQUEST_CANCELLED',
          status: 0,
          timestamp,
          path,
          details: {
            requestId,
          },
        };
      } else if (error instanceof TypeError && error.message.includes('NetworkError')) {
        apiError = {
          message: 'Network error occurred',
          code: 'CLIENT_NETWORK_ERROR',
          status: 0,
          timestamp,
          path,
          details: {
            requestId,
          },
        };
      } else if ((error as ApiError).message && (error as ApiError).code) {
        apiError = error as ApiError;
        // Ensure all required fields are present
        if (!apiError.timestamp) apiError.timestamp = timestamp;
        if (!apiError.path) apiError.path = path;
        if (!apiError.details) apiError.details = { requestId };
        else if (!apiError.details.requestId) apiError.details.requestId = requestId;
      } else {
        apiError = {
          message: 'Unknown error occurred',
          code: 'CLIENT_UNKNOWN_ERROR',
          status: 0,
          timestamp,
          path,
          details: {
            requestId,
            error: error instanceof Error ? error.message : String(error),
          },
        };
      }

      // Apply error interceptors
      let processedError = apiError;
      for (const interceptor of this.errorInterceptors) {
        const result = await interceptor(processedError);
        if ('data' in result) {
          // If an interceptor returns a response, return it
          return result as ApiResponse<T>;
        }
        processedError = result;
      }

      // Notify error handlers
      this.notifyErrorHandlers(processedError, processedConfig);

      throw processedError;
    } finally {
      // Clean up
      clearTimeout(timeoutId);
      this.activeRequests.delete(requestId);

      // Notify loading state handlers
      this.notifyLoadingStateHandlers(false, requestId);
    }

  }

  /**
   * Generates a unique key for a request to prevent duplicate requests
   * @param method - HTTP method
   * @param url - Request URL
   * @param data - Request data
   * @param authHeader - Authorization header
   * @returns A unique key for the request
   */
  private generateRequestKey(method: string, url: string, data?: unknown, authHeader?: string): string {
    // Include the Authorization header in the key to ensure requests with different
    // authentication states are treated as different requests
    const authPart = authHeader ? `auth:${authHeader.substring(0, 10)}...` : 'no-auth';

    // For GET requests, include the URL and auth state
    if (method === HttpMethod.GET) {
      return `${method}:${url}:${authPart}`;
    }

    // For other methods, include a hash of the data and auth state
    const dataString = data ? JSON.stringify(data) : '';
    return `${method}:${url}:${dataString}:${authPart}`;
  }

  /**
   * Builds a full URL with query parameters
   * @param path - The URL path
   * @param params - Query parameters
   * @returns The full URL
   */
  private buildUrl(path: string, params?: Record<string, string | number | boolean | undefined>): string {
    // Ensure path doesn't start with a slash if baseURL ends with one
    const normalizedPath = this.baseURL.endsWith('/') && path.startsWith('/')
      ? path.substring(1)
      : path;

    // Combine baseURL and path
    let url = `${this.baseURL}${normalizedPath}`;

    // Add query parameters if provided
    if (params) {
      const queryParams = new URLSearchParams();

      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, String(value));
        }
      });

      const queryString = queryParams.toString();
      if (queryString) {
        url += (url.includes('?') ? '&' : '?') + queryString;
      }
    }

    return url;
  }

  /**
   * Parses response headers into a simple object
   * @param headers - Response headers
   * @returns Headers as a simple object
   */
  private parseHeaders(headers: Headers): Record<string, string> {
    const result: Record<string, string> = {};
    headers.forEach((value, key) => {
      result[key] = value;
    });
    return result;
  }

  /**
   * Creates an API error from a response
   * @param response - The API response
   * @returns An API error
   */
  private createApiError(response: ApiResponse): ApiError {
    // Extract data from response
    const responseData = response.data || {};

    // Create error object according to the expected structure
    const error: ApiError = {
      status: response.status,
      code: responseData.code || `HTTP_${response.status}`,
      number: responseData.number,
      message: responseData.message || response.statusText || 'Request failed',
      timestamp: responseData.timestamp,
      path: responseData.path,
      details: responseData.details || {},
    };

    // Ensure requestId is always present in details
    if (error.details && !error.details.requestId) {
      error.details.requestId = `client-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    }

    return error;
  }

  /**
   * Notifies loading state handlers
   * @param isLoading - Whether the request is loading
   * @param requestId - The ID of the request
   */
  private notifyLoadingStateHandlers(isLoading: boolean, requestId?: string): void {
    for (const handler of this.loadingStateChangeHandlers) {
      handler(isLoading, requestId);
    }
  }

  /**
   * Notifies success handlers
   * @param response - The API response
   * @param config - The request configuration
   */
  private notifySuccessHandlers<T>(response: ApiResponse<T>, config: RequestConfig): void {
    for (const handler of this.successNotificationHandlers) {
      handler(response, config);
    }
  }

  /**
   * Notifies error handlers
   * @param error - The API error
   * @param config - The request configuration
   */
  private notifyErrorHandlers(error: ApiError, config: RequestConfig): void {
    for (const handler of this.errorNotificationHandlers) {
      handler(error, config);
    }
  }
}
